import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '@/entities/settings';
import { CheckboxSettingsPopover } from '@/shared/ui';

/** Опция таба для настройки видимости (ключ + отображаемое имя). */
export type DialogDataTabOption = {
  key: string;
  title: string;
};

type Props = {
  /** Все табы, доступные в текущей секции (включая динамические экспертные отчёты). */
  options: DialogDataTabOption[];
};

/**
 * Иконка-кнопка с Popover'ом для управления видимостью табов секции данных диалога.
 *
 * Состояние видимости персистируется в `useSettingsStore` как `dialogsPage.hiddenDataTabs`
 * (список скрытых ключей) — отсутствие ключа в списке = таб видим. Настраиваются только табы
 * открытой записи, поэтому вернуть таб в показ можно там, где он есть.
 */
export const DialogDataTabsSettings: FC<Props> = ({ options }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { dialogsPage, setDialogsSettings } = useSettings();
  const hiddenDataTabs = useMemo(() => dialogsPage?.hiddenDataTabs ?? [], [dialogsPage]);

  // В CheckboxGroup храним инвертированно — список ВИДИМЫХ ключей,
  // чтобы UX был «отметь, что показывать»
  const visibleKeys = useMemo(
    () => options.map((opt) => opt.key).filter((key) => !hiddenDataTabs.includes(key)),
    [options, hiddenDataTabs],
  );

  const checkboxOptions = useMemo(() => options.map((opt) => ({ value: opt.key, label: opt.title })), [options]);

  const handleChange = useCallback(
    (nextVisible: string[]) => {
      // Состав табов открытой записи — не полный перечень решений пользователя: отчёт есть
      // вкладкой только там, где он построен. Поэтому скрытые ключи, которых в записи нет,
      // сохраняются как есть — иначе настройка в одной записи возвращала бы в показ то,
      // что убрано в другой.
      const currentKeys = options.map((opt) => opt.key);
      const nextHidden = [
        ...hiddenDataTabs.filter((key) => !currentKeys.includes(key)),
        ...currentKeys.filter((key) => !nextVisible.includes(key)),
      ];
      setDialogsSettings({ hiddenDataTabs: nextHidden });
    },
    [options, hiddenDataTabs, setDialogsSettings],
  );

  return (
    <CheckboxSettingsPopover
      title={t('dialogs:dataTabsSettings')}
      name="dialog-data-tabs-visibility"
      options={checkboxOptions}
      value={visibleKeys}
      onChange={handleChange}
    />
  );
};
